import { useEffect } from 'react';
import { AccessTokenState, Space, User } from '@core/types/state';

export type UseFetchAssignmentsParams = {
  accessTokens: AccessTokenState;
  fetchAccessToken: ({
    spaceId,
  }: {
    spaceId: string;
  }) => Promise<string | null>;
  fetchAssignments: (params: {
    user: User | null;
    accessToken: string;
    spaceId: string;
  }) => void;
  isFetching: boolean;
  needsToRefreshAccessTokenProp: () => boolean;
  selectedSpace?: Space;
  user: User | null;
};

/**
 * Resolves an access token for the selected space and loads its assignments.
 * Shared by the assignments and submissions responsive pages so both render the
 * same sidebar list without duplicating the token refresh logic.
 */
const useFetchAssignments = ({
  accessTokens,
  fetchAccessToken,
  fetchAssignments,
  isFetching,
  needsToRefreshAccessTokenProp,
  selectedSpace,
  user,
}: UseFetchAssignmentsParams) => {
  const selectedSpaceId = selectedSpace?.objectId;

  useEffect(() => {
    const resolveAccessToken = async (spaceId: string) => {
      if (needsToRefreshAccessTokenProp()) {
        return fetchAccessToken({ spaceId });
      }

      return accessTokens[spaceId]?.token || fetchAccessToken({ spaceId });
    };

    const loadAssignments = async () => {
      if (isFetching || !selectedSpaceId) {
        return;
      }

      const accessToken = await resolveAccessToken(selectedSpaceId);

      if (accessToken) {
        fetchAssignments({
          user,
          accessToken,
          spaceId: selectedSpaceId,
        });
      }
    };

    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceId]);
};

export default useFetchAssignments;
